import GameEvents from "../const/GameEvents";
import MathUtil from "../utils/MathUtil";
import SoundVars from "../const/SoundVars";
import SoundController from "../utils/SoundController";
import Reel from "../views/Reel";
import App from "../App";
import { TweenMax, Tween, Power2, Cubic } from "gsap";
import spinModel from "../model/SpinModel";
import Layout from "../utils/Layout";
import * as Easings from "../utils/Easings";
import Utils from "../utils/Utils";
import Layer from "../utils/Layer";

/**
 * Animate reel symbols with mechanical spin animation.
 * PID stands for Proportional Integral Differential - derived from PID controllers. For more info :
 * https://en.wikipedia.org/wiki/PID_controller
 * To stop the reel a PID Controller approach is used where we compare the desired position to the current position
 * and apply control based on the difference.
 * The controlled variable is the reel's speed.
 */
export default abstract class AbstractReelBehaviour {
    /**
     * @constructor
     * @param reel {Reel} - the reel to be controlled
     */
    protected reel: Reel; // TODO interface for that
    protected isSpinning: boolean;
    protected hasAnticipation: boolean;
    protected yPos: number;
    protected yPosFinal: number;
    protected speed: number;
    protected maxSpeed: number;
    protected spinTweener = {};
    protected rollSnd: Howl;
    protected errors: any[];
    protected errorDeltas: any[];
    protected unmaskThreshold: number;
    protected disableUnmasking: boolean;
    protected stopPhase: boolean;
    protected errorIndex: number;
    protected stopSoundPlayed: boolean;
    protected timeScale: number;
    protected stopSymbols: any[];
    protected moveBackTween: Tween;
    protected speedUpTween: Tween;
    protected spinUpdateTween: Tween;
    protected spinifyTween: Tween;
    protected Kp: number;
    protected Ki: number;
    protected currTime: number;
    protected direction: number;
    protected currStopTime: number;
    protected stopTime: number;
    protected _stopTime: number;
    protected _maxStopTime: number;
    protected maxStopTime: number;
    protected stopDistance: number;
    protected sortFunction: () => void;
    protected easingFunction: any;
    protected hitFunction: (symId: number, reelId: number, rowId: number) => void;
    protected spinSound: string;
    protected stopSound: string;

    constructor(reel: Reel, sortFunction: () => void, stopTime: number =  App.config.stopTime, maxStopTime: number = App.config.maxStopTime) {
        // this.app = PIXI.app;
        this.sortFunction = sortFunction;
        this.reel = reel;
        this.stopTime = stopTime || 0.8 ;
        this._stopTime = this.stopTime ;
        this.maxStopTime = maxStopTime ;
        this._maxStopTime = maxStopTime ;
        this.isSpinning = false;
        this.hasAnticipation = false;
        this.yPos = 0;
        this.speed = 0;
        this.maxSpeed = App.config.maxReelSpeed;
        this.spinTweener = {};
        this.errors = [];
        this.errorDeltas = [];
        this.unmaskThreshold =
            App.config.marginTop +
            this.reel.symStep *
                (this.reel.numSyms - this.reel.getExtraSymbolsCount()) +
            this.reel.yOrigin;
        this.disableUnmasking = App.config.disableUnmasking;
        this.direction = 1;
        this.spinSound = SoundVars.EFFECT_REEL_SPIN;
        this.stopSound = SoundVars.EFFECT_REEL_STOP;
    }

    public setSounds(sounds:{spin?:string,stop?:string}) {
        this.spinSound = sounds.spin || this.spinSound;
        this.stopSound = sounds.stop || this.stopSound;
    }

    public flipDirection() {
        this.direction = -this.direction;
    }

    public activateAnticipation() {
        // TODO - when refactoring spinner the hasAnticipation property should initiate diferent stoping
        this.hasAnticipation = true;
        this.spinifyTween = TweenMax.to(this, 1, {speed: App.config.spinificationSpeed || this.maxSpeed / 2 , ease: Power2.easeInOut});
    }

    public anticipationStarted() {
        return this.hasAnticipation;
    }

    public anticipationEnded() {
        return !this.isSpinning;
    }


    public isSettled() {
        return !this.isSpinning;
    }

    public getStopAnimationDuration() {
        return this.stopTime;
    }


    public changeEase(stopTime:number,easingFunction:any = Easings.EaseInOutBack) {
        this.stopTime = stopTime || 0.8 ;
        this.maxStopTime = stopTime*2;
        this.easingFunction = easingFunction;
    }
    /**
     * Start the spin animation.
     * @public
     */
    public spin(easingFunction:any = Easings.EaseOutBack) {
        if (this.isSpinning === false) {
            this.isSpinning = true;
            this.hasAnticipation = false;
            this.rollSnd = SoundController.playSound(
                this.spinSound,
                true,
                undefined,
                App.config.splitSpinSound
            );

            this.easingFunction = easingFunction;
            this.stopPhase = false;
            this.stopTime = this._stopTime;
            this.maxStopTime = this._maxStopTime;
            this.errorIndex = 0;
            this.yPos = 0;
            this.speed = 0;
            this.stopSoundPlayed = false;
            this.timeScale = 1;
            this.stopSymbols = null;

            this.moveBackTween = TweenMax.to(this, 0.08, {
                speed: -this.maxSpeed / 30,
                ease: Power2.easeInOut,
            });
            this.speedUpTween = TweenMax.to(this, 0.3, {
                delay: 0.08,
                speed: this.maxSpeed,
                ease: Power2.easeIn,
            });

            this.spinUpdateTween = TweenMax.to(this.spinTweener, 10000000, {
                onUpdate: this.spinUpdate,
                onUpdateScope: this,
            });
            this.currTime = this.spinUpdateTween.time();

            // this.reel.unmaskSymbols();
            this.reel.resetSymbolsForSpin();

            this.Kp = App.config.Kp;
            this.Ki = App.config.Ki;
        }
    }

    /**
     * Stops the animation on the required final symbols.
     * @public
     * @param stopSymbolData {Array} - symbol ids of the symbols to stop on
     */
    public stop(stopSymbolData: number[], hasAnticipation = false, stopTime?: number) {
        // debugger;
        this.reel.emit(GameEvents.REEL_STOPPING, this.reel);
        const stopSymbolIds = [];
        this.stopTime = stopTime || this.stopTime;
        this.maxStopTime = stopTime || this.maxStopTime;
        for (let symIdx = 0; symIdx < stopSymbolData.length; symIdx++) {
            stopSymbolIds.push(stopSymbolData[symIdx]);
        }

        this.stopSymbols = stopSymbolIds.slice();
        if (!this.stopPhase) {
            this.stopPhase = true;
            this.currStopTime = 0;

            if (this.moveBackTween) {
                this.moveBackTween.kill();
                this.moveBackTween = null;
            }

            // if (this.speedUpTween) {
                // this.speedUpTween.kill();
                // this.speedUpTween = null;
            // }

            if (this.spinifyTween) {
                this.spinifyTween.kill();
                this.spinifyTween = null;
            }

            const numSyms = this.reel.numSyms;

            let extraSyms = this.reel.getExtraSymbolsCount() * 2;

            this.yPosFinal =
                Math.ceil(
                    (this.yPos +
                        Layout.scalePixels(App.config.marginTop) * this.direction +
                        this.reel.symStep *
                            (numSyms + extraSyms) *
                            this.direction) /
                        this.reel.symStep
                ) * this.reel.symStep;

            this.stopDistance = this.yPosFinal - this.yPos;

            this.currTime = this.spinUpdateTween.time();


        }
    }

    /**
     * @public
     * Sets the parameters that control the stopping animation so reel stops as fast as possible.
     */
    public activateQuickStop() {

        this.Kp = App.config.fastStopKp;
        this.Ki = App.config.fastStopKi;
        this.timeScale = 1.25;

    }

    /**
     * Update function for the spin animation
     * @private
     */
    spinUpdate() {
        // Calculate delta
        const newTime = this.spinUpdateTween.time();
        const deltaSec = (newTime - this.currTime) * this.timeScale;
        this.currTime = newTime;


        // Var init
        let yStep = Layout.scalePixels(this.speed * deltaSec);
        const diff = this.yPosFinal - this.yPos;
        let finalUpdate = false;

        // this.rollSnd.volume(this.speed / this.maxSpeed * Number(SoundController.isSoundEnabled));

        if (this.stopPhase === true) {


            // In the stop phase the reel slows down and sets the final symbols

            this.currStopTime += deltaSec;



            //////\\\\\\\\\\

            // These calculations are for the integral part of the controller. They accumulate error over time.
            // We use the integral error because we want to overshoot our target position and create
            // a mechanical feeling to the movement
            // this.errors[this.errorIndex] = diff;
            // this.errorDeltas[this.errorIndex] = deltaSec;

            // let integrationTime = 0;
            // let totalError = 0;

            // const maxIntegrationTime = App.config.maxIntegrationTime;

            // for (let errIdx = this.errorIndex; errIdx >= 0; errIdx--) {
            //     const err = this.errors[errIdx];
            //     const delta = this.errorDeltas[errIdx];

            //     if (integrationTime + deltaSec > maxIntegrationTime) {
            //         const deltaFraction =
            //             (maxIntegrationTime - integrationTime) / delta;
            //         totalError += err * deltaFraction;
            //         break;
            //     } else {
            //         totalError += err * delta;
            //     }

            //     integrationTime += deltaSec;
            // }
            // this.errorIndex++;

            // // Modify the speed using proportional and integral components
            // // When we get closer to the final position the error (diff) gets smaller , so the speed gets smaller
            // this.speed = diff * this.Kp + totalError * this.Ki;


            //////\\\\\\

            let progress = Math.min(this.currStopTime,this.stopTime ) / this.stopTime ;
            let progressEase = this.easingFunction(progress);
            const targetY = this.yPosFinal - (1 - progressEase) * this.stopDistance;
            yStep = Math.min(Layout.scalePixels(targetY - this.yPos), yStep);


            // console.log("[SPINNER]",progress,this.stopTime,this.stopDistance,this.currStopTime );

            this.Ki *= App.config.KiDamp;
            this.speed = MathUtil.clamp(
                this.speed,
                -this.maxSpeed,
                this.maxSpeed
            );

            // Force finish if max time is reached or if the reel movement is very small (animation is over)
            // let minDistance = Layout.scalePixels()
            // let minDistance = 0.01;
            let minDistance = Layout.scalePixels(0.1);
            let minSoundDistance = Layout.scalePixels(0.2);
            const reelSettled = Math.abs(yStep) < minDistance && Math.abs(diff) < minDistance;
            const soundSettled = this.stopTime - this.currStopTime <= (App.config.stopSoundOffset || minSoundDistance) || progress > 0.99;
            if (this.currStopTime >= this.maxStopTime || reelSettled) {
                yStep = this.yPosFinal - this.yPos;
                finalUpdate = true;
            }

            if (
                this.stopSoundPlayed === false &&
                progress >= Math.min((0.55 + 1 - this.speed / this.maxSpeed), 0.9) &&
                soundSettled
                // this.speed < this.maxSpeed * 0.2
            ) {
                this.stopSoundPlayed = true;
                if (!spinModel.stopImmediately) {
                    SoundController.playSound(
                        this.stopSound,
                        false
                    );
                    App.dispatcher.emit(
                        GameEvents.UPDATE_SPINIFICATION,
                        this.reel.reelIndex + this.reel.getExtraSymbolsCount()
                    );
                } else if (
                    spinModel.stopImmediately &&
                    this.reel.reelIndex == 0
                ) {
                    SoundController.playSound(
                        this.stopSound,
                        false
                    );
                }
            }
        } else {
            yStep *= this.direction;
        }

        this.yPos += yStep;

        const numSyms = this.reel.symbols.length;
        for (let symIdx = 0; symIdx < numSyms; symIdx++) {
            const s = this.reel.symbols[symIdx];
            s.setBlurLevel(finalUpdate ? 0 : this.speed / this.maxSpeed);
            s.y += yStep;

            const wrapToBottom =
                s.y > this.reel.reelBottom + this.reel.yOrigin && yStep > 0;
            const wrapToTop = s.y < -s.height + this.reel.yOrigin && yStep < 0;

            if (wrapToBottom || wrapToTop) {
                const sign = wrapToTop ? 1 : -1;
                s.y = s.y + sign * this.reel.symbolWrapDistance;
                s.visible = true;
                // s.mask = this.reel.maskGraphics;

                if (this.stopPhase === true) {
                    let idx = Math.floor(
                        (this.yPosFinal - this.yPos + s.y - this.reel.yOrigin) /
                            this.reel.symStep
                    );
                    idx += this.reel.getExtraSymbolsCount();

                    if (idx >= 0 && idx < this.stopSymbols.length) {
                        s.setId(this.stopSymbols[idx]);
                        // s.mask = null;
                        s.isFinal = true;
                    } else if (idx === -1 || idx === this.reel.numSyms) {
                        // s.setId(spinModel.getNextStopPosition(this.reel.reelIndex));
                        s.setId(
                            this.stopSymbols[
                                Math.abs(
                                    Math.abs(idx) - this.stopSymbols.length
                                )
                            ]
                        );
                    }

                    // if(idx >  this.reel.getExtraSymbolsCount() ) {
                    //     s.mask = null;
                    // }

                    if ( s.isFinal && idx > this.reel.getExtraSymbolsCount() && idx <= (numSyms - this.reel.getExtraSymbolsCount()))
                    {
                        s.alpha = 1;
                        if (!this.disableUnmasking)
                        {
                            // s.mask = null;
                        }
                    } else {
                        // s.mask = this.reel.mask;
                    }
                    // else
                    // {
                    //     s.setId(spinModel.getNextStopPosition(this.reel.reelIndex));
                    // }
                } else if (!s.isFinal) {
                    if (this.hasAnticipation && App.config.phantomSymbols && Utils.calculateChance(App.config.phantomChance || 50)) {
                        let symId = Utils.getRandomEl(App.config.phantomSymbols);
                        s.setId(symId);
                    } else {
                        s.setId(spinModel.getNextStopPosition(this.reel.reelIndex));
                    }
                }

                if (this.sortFunction) {
                    this.sortFunction();
                }
            }

            if (s.isFinal && yStep < 0 && !this.disableUnmasking) {
                // s.mask = null;
            }

            if (s.y > this.unmaskThreshold) {
                if (s.isFinal === true && !this.disableUnmasking) {
                    // s.mask = null;
                } else if (yStep > 0) {
                    // s.mask = this.reel.maskGraphics;
                }
            }
        }

        if (finalUpdate === true) {
            // this.rollSnd.volume(0);
            if (this.rollSnd) {
                // this.rollSnd.stop();
                SoundController.stopSound(this.rollSnd);
            }
            let minY = Number.POSITIVE_INFINITY;
            let maxY = Number.NEGATIVE_INFINITY;
            let bottomSymId;
            let topSymId;

            this.reel.sortSymbolsByY();

            for (let symIdx = 0; symIdx < numSyms; symIdx++) {
                const s = this.reel.symbols[symIdx];

                if (s.y < minY) {
                    bottomSymId = s.getId();
                    minY = s.y;
                }

                if (s.y > maxY) {
                    topSymId = s.getId();
                    maxY = s.y;
                }
            }

            for (let symIdx = 0; symIdx < numSyms; symIdx++) {
                const s = this.reel.symbols[symIdx];
                s.y =
                    this.reel.symStep *
                        (-this.reel.getExtraSymbolsCount() + symIdx) +
                    Layout.scalePixels(this.reel.symBounds.height) * 0.5 +
                    this.reel.yOrigin;

                // if(this.stopSymbols[symIdx]) {

                s.visible = true;
                s.alpha = 1;
                s.setId(this.stopSymbols[symIdx]);
                this.playHitAction(this.stopSymbols[symIdx],this.reel.reelIndex,symIdx);
                // }

                if (App.config.hideExtraSymbols) {
                    if (symIdx >= this.reel.getExtraSymbolsCount() && symIdx < (numSyms - this.reel.getExtraSymbolsCount()))
                    {
                        s.alpha = 1;
                        if (!this.disableUnmasking)
                        {
                            // s.mask = null;
                        }
                    }
                    else if (symIdx === this.reel.getExtraSymbolsCount() - 1)
                    {
                        s.setId(bottomSymId);
                        // s.mask = this.reel.maskGraphics;
                        s.alpha = 0;
                    }
                    else if ( symIdx === (numSyms - this.reel.getExtraSymbolsCount() - 1) )
                    {
                        s.setId(topSymId);
                        // s.mask = this.reel.maskGraphics;
                        s.alpha = 0;
                    } else {
                        s.alpha = 0;
                    }

                }

            }

            if (this.sortFunction) {
                this.sortFunction();
            }
            this.reel.unmaskSymbols();
            this.spinUpdateTween.kill();

            if (this.speedUpTween) {
                this.speedUpTween.kill();
                this.speedUpTween = null;
            }


            this.isSpinning = false;
            this.reel.emit(GameEvents.REEL_STOPPED, this.reel);
        }
    }
    setCustomHit(hitFunction:(symId: number, reelId: number, rowId: number) => void) {
        this.hitFunction = hitFunction;
    }

    protected playHitAction(symId: number, reelId: number, rowId: number) {
        if ( rowId >= this.reel.getExtraSymbolsCount() &&
            rowId <= this.reel.numSyms
        ) {

            if (this.hitFunction) {
                this.hitFunction(symId, reelId, rowId);
            }
            if (App.config.hitSounds && App.config.hitSounds[symId]) {
                SoundController.playSound(App.config.hitSounds[symId]);
            }
        }
    }

    // public on() {
    //     return this.
    // }
    public on(event:string,fn: () => any,context?:any) {
        this.reel.on(event,fn,context);
    }

    public off(event:string,fn: () => any,context?:any) {
       this.reel.off(event,fn,context);
    }

    public once(event:string,fn: () => any,context?:any) {
         this.reel.once(event,fn,context);
    }
}
